import { SKIP_AUTH_HEADER, request } from './request'
import { useServerFetch } from '../composables/useServerFetch'
import { useEnvApi } from '../composables/useEnvApi'

export const REGULATOR_LIST_API_PATH = '/api/v4/license/bit/web/regulator/list'
export const REGULATOR_DETAIL_API_PATH = '/api/v4/bit/web/regulator/detail'
export const REGULATOR_CODE_BY_EN_SHORT_NAME_API_PATH = '/api/v4/license/regulator/info/by-urlslug-or-code'

export type RegulatorListAreaItem = {
  areaCodeType: string
  areaCode: string
  areaName: string
}

export type RegulatorListItem = {
  regulatorId: string
  regulatorCode: string
  urlSlug: string
  regulatorShowName: string
  regulatorDescription: string
  foundationDate: string
  countryCode: string
  countryName: string
  countryFlag: string
  isOffshore: string
  logo: string
}

export type RegulatorListData = {
  items: RegulatorListItem[]
  areaItems: RegulatorListAreaItem[]
  regulatorsDescription: string
}

export type RegulatorDetailNote = {
  regulationIcon: string
  shortDescription: string
  regulationBottomRightIcon: string
  regulationBottomText: string
}

export type RegulatorDetailNotePopup = {
  infoIcon: string
  infoTitle: string
  rightRequirementText: string
  infoText: string
}

export type RegulatorDetailDisclosureBroker = {
  brokerIcon: string
  brokerCode: string
  brokerShortName: string
}

export type RegulatorDetailDisclosure = {
  titleText: string
  brokerList: RegulatorDetailDisclosureBroker[]
  time: string
  disclosureId: string
}

export type RegulatorDetailBrokerTag = {
  text: string
  textColor: string
}

export type RegulatorDetailBroker = {
  brokerIcon: string
  brokerCode: string
  brokerShortName: string
  alias: string
  score: string
  ultimateUltraIco?: string
  tags: RegulatorDetailBrokerTag[]
  businessTypes: string
}

export type RegulatorDetailLinkItem = {
  icon: string
  text: string
  type: string
  url: string
}

export type RegulatorDetailComplaintItem = {
  icon: string
  text: string
  contactContent: string
  type: string
}

export type RegulatorDetailData = {
  isFollowed: boolean
  regulatorImage: string
  regulatorName: string
  regulatorShortName: string
  shortName: string
  regulatorIcon: string
  regulatorCode: string
  urlSlug: string
  regulatorHeaderText: string
  regulatorCountry: string
  regulatorCountryIco: string
  regulatorEstablishedText: string
  foundationDate: string
  websites: string[]
  regulatorDescription: string
  regulationNotes: RegulatorDetailNote[]
  regulationNotePopupList: RegulatorDetailNotePopup[]
  disclosureCount: number
  disclosureList: RegulatorDetailDisclosure[]
  memberCount: number
  memberList: RegulatorDetailBroker[]
  blacklistCount: number
  blacklistList: RegulatorDetailBroker[]
  licenseApplicationList: RegulatorDetailLinkItem[]
  complaintChannelList: RegulatorDetailComplaintItem[]
}

export type RegulatorListParams = {
  areaCodeType?: string
  areaCode?: string
  pageindex?: number
  pagesize?: number
}

type RegulatorListResponse = {
  code?: number
  data?: Partial<RegulatorListData>
  message?: string
  succeed?: boolean
}

type RegulatorDetailResponse = {
  code?: number
  data?: Partial<RegulatorDetailData>
  message?: string
  succeed?: boolean
}

type RegulatorCodeResponse = {
  regulatorId?: string
  urlSlug?: string
  regulatorCode?: string
  data?: {
    regulatorId?: string
    urlSlug?: string
    regulatorCode?: string
  }
  code?: number
  message?: string
  succeed?: boolean
}

const EMPTY_REGULATOR_LIST: RegulatorListData = {
  items: [],
  areaItems: [],
  regulatorsDescription: '',
}

const normalizeText = (value: unknown) => {
  return value === null || value === undefined ? '' : String(value).trim()
}

/** 标准化接口中的网址数组，过滤空值以确保页面首项可直接使用。 */
const normalizeStringList = (value: unknown) => {
  if (!Array.isArray(value)) {
    return []
  }

  return value.map((item) => normalizeText(item)).filter(Boolean)
}

const normalizeRegulatorItem = (item: Partial<RegulatorListItem>): RegulatorListItem => {
  return {
    regulatorId: normalizeText(item.regulatorId),
    regulatorCode: normalizeText(item.regulatorCode),
    urlSlug: normalizeText(item.urlSlug),
    regulatorShowName: normalizeText(item.regulatorShowName),
    regulatorDescription: normalizeText(item.regulatorDescription),
    foundationDate: normalizeText(item.foundationDate),
    countryCode: normalizeText(item.countryCode),
    countryName: normalizeText(item.countryName),
    countryFlag: normalizeText(item.countryFlag),
    isOffshore: normalizeText(item.isOffshore),
    logo: normalizeText(item.logo),
  }
}

const normalizeAreaItem = (item: Partial<RegulatorListAreaItem>): RegulatorListAreaItem => {
  return {
    areaCodeType: normalizeText(item.areaCodeType),
    areaCode: normalizeText(item.areaCode),
    areaName: normalizeText(item.areaName),
  }
}

const normalizeNumber = (value: unknown) => {
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : 0
}

const normalizeRegulatorList = (response: RegulatorListResponse | null): RegulatorListData => {
  const data = response?.data
  if (!data) {
    return EMPTY_REGULATOR_LIST
  }

  return {
    items: Array.isArray(data.items) ? data.items.map(normalizeRegulatorItem) : [],
    areaItems: Array.isArray(data.areaItems) ? data.areaItems.map(normalizeAreaItem) : [],
    regulatorsDescription: normalizeText(data.regulatorsDescription),
  }
}

const normalizeRegulationNote = (item: Partial<RegulatorDetailNote>): RegulatorDetailNote => {
  return {
    regulationIcon: normalizeText(item.regulationIcon),
    shortDescription: normalizeText(item.shortDescription),
    regulationBottomRightIcon: normalizeText(item.regulationBottomRightIcon),
    regulationBottomText: normalizeText(item.regulationBottomText),
  }
}

const normalizeRegulationNotePopup = (item: Partial<RegulatorDetailNotePopup>): RegulatorDetailNotePopup => {
  return {
    infoIcon: normalizeText(item.infoIcon),
    infoTitle: normalizeText(item.infoTitle),
    rightRequirementText: normalizeText(item.rightRequirementText),
    infoText: normalizeText(item.infoText),
  }
}

const normalizeDisclosureBroker = (item: Partial<RegulatorDetailDisclosureBroker>): RegulatorDetailDisclosureBroker => {
  return {
    brokerIcon: normalizeText(item.brokerIcon),
    brokerCode: normalizeText(item.brokerCode),
    brokerShortName: normalizeText(item.brokerShortName),
  }
}

const normalizeDisclosureItem = (item: Partial<RegulatorDetailDisclosure>): RegulatorDetailDisclosure => {
  return {
    titleText: normalizeText(item.titleText),
    brokerList: Array.isArray(item.brokerList) ? item.brokerList.map(normalizeDisclosureBroker) : [],
    time: normalizeText(item.time),
    disclosureId: normalizeText(item.disclosureId),
  }
}

const normalizeBrokerTag = (item: Partial<RegulatorDetailBrokerTag>): RegulatorDetailBrokerTag => {
  return {
    text: normalizeText(item.text),
    textColor: normalizeText(item.textColor),
  }
}

const normalizeBrokerItem = (item: Partial<RegulatorDetailBroker>): RegulatorDetailBroker => {
  return {
    brokerIcon: normalizeText(item.brokerIcon),
    brokerCode: normalizeText(item.brokerCode),
    brokerShortName: normalizeText(item.brokerShortName),
    alias: normalizeText(item.alias),
    score: normalizeText(item.score),
    ultimateUltraIco: normalizeText(item.ultimateUltraIco),
    tags: Array.isArray(item.tags) ? item.tags.map(normalizeBrokerTag) : [],
    businessTypes: normalizeText(item.businessTypes),
  }
}

const normalizeLinkItem = (item: Partial<RegulatorDetailLinkItem>): RegulatorDetailLinkItem => {
  return {
    icon: normalizeText(item.icon),
    text: normalizeText(item.text),
    type: normalizeText(item.type),
    url: normalizeText(item.url),
  }
}

const normalizeComplaintItem = (item: Partial<RegulatorDetailComplaintItem>): RegulatorDetailComplaintItem => {
  return {
    icon: normalizeText(item.icon),
    text: normalizeText(item.text),
    contactContent: normalizeText(item.contactContent),
    type: normalizeText(item.type),
  }
}

const normalizeRegulatorDetail = (response: RegulatorDetailResponse | RegulatorDetailData | null) => {
  const source = (response && 'data' in response ? response.data : response) as Partial<RegulatorDetailData> | null
  if (!source) {
    return null
  }

  return {
    isFollowed: Boolean(source.isFollowed),
    regulatorImage: normalizeText(source.regulatorImage),
    regulatorName: normalizeText(source.regulatorName),
    regulatorShortName: normalizeText(source.regulatorShortName),
    shortName: normalizeText(source.shortName),
    regulatorIcon: normalizeText(source.regulatorIcon),
    regulatorCode: normalizeText(source.regulatorCode),
    urlSlug: normalizeText(source.urlSlug),
    regulatorHeaderText: normalizeText(source.regulatorHeaderText),
    regulatorCountry: normalizeText(source.regulatorCountry),
    regulatorCountryIco: normalizeText(source.regulatorCountryIco),
    regulatorEstablishedText: normalizeText(source.regulatorEstablishedText),
    foundationDate: normalizeText(source.foundationDate),
    websites: normalizeStringList(source.websites),
    regulatorDescription: normalizeText(source.regulatorDescription),
    regulationNotes: Array.isArray(source.regulationNotes) ? source.regulationNotes.map(normalizeRegulationNote) : [],
    regulationNotePopupList: Array.isArray(source.regulationNotePopupList)
      ? source.regulationNotePopupList.map(normalizeRegulationNotePopup)
      : [],
    disclosureCount: normalizeNumber(source.disclosureCount),
    disclosureList: Array.isArray(source.disclosureList) ? source.disclosureList.map(normalizeDisclosureItem) : [],
    memberCount: normalizeNumber(source.memberCount),
    memberList: Array.isArray(source.memberList) ? source.memberList.map(normalizeBrokerItem) : [],
    blacklistCount: normalizeNumber(source.blacklistCount),
    blacklistList: Array.isArray(source.blacklistList) ? source.blacklistList.map(normalizeBrokerItem) : [],
    licenseApplicationList: Array.isArray(source.licenseApplicationList)
      ? source.licenseApplicationList.map(normalizeLinkItem)
      : [],
    complaintChannelList: Array.isArray(source.complaintChannelList)
      ? source.complaintChannelList.map(normalizeComplaintItem)
      : [],
  } satisfies RegulatorDetailData
}

/** 获取监管机构列表，地区筛选参数与 BFF areaItems 返回值保持一致。 */
export const getRegulatorList = async (params: RegulatorListParams = {}): Promise<RegulatorListData> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: REGULATOR_LIST_API_PATH,
    method: 'GET' as const,
    params,
    headers: {
      [SKIP_AUTH_HEADER]: '1',
    },
  }

  const response = import.meta.server
    ? await useServerFetch<RegulatorListResponse>(requestConfig)
    : await request<unknown, RegulatorListResponse>(requestConfig)

  return normalizeRegulatorList(response)
}

/** 获取监管机构详情，接口异常时返回 null，页面负责展示空态。 */
export const getRegulatorDetail = async (regulatorCode: string): Promise<RegulatorDetailData | null> => {
  const normalizedRegulatorCode = normalizeText(regulatorCode)
  if (!normalizedRegulatorCode) {
    return null
  }

  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: REGULATOR_DETAIL_API_PATH,
    method: 'GET' as const,
    params: {
      urlSlug: normalizedRegulatorCode,
    },
    headers: {
      [SKIP_AUTH_HEADER]: '1',
    },
  }

  try {
    const response = import.meta.server
      ? await useServerFetch<RegulatorDetailResponse | RegulatorDetailData>(requestConfig)
      : await request<unknown, RegulatorDetailResponse | RegulatorDetailData>(requestConfig)

    return normalizeRegulatorDetail(response)
  } catch {
    return null
  }
}

type RegulatorCodeResolveParams = {
  urlSlug?: string
  regulatorCode?: string
  regulatorId?: string | number
}

/** 根据监管机构 urlSlug、code 或 id 解析监管机构标识。 */
const getRegulatorResolveInfo = async (
  params: RegulatorCodeResolveParams | string,
): Promise<RegulatorCodeResponse['data']> => {
  const normalizedParams =
    typeof params === 'string'
      ? { urlSlug: normalizeText(params) }
      : {
          urlSlug: normalizeText(params.urlSlug),
          regulatorCode: normalizeText(params.regulatorCode),
          regulatorId: normalizeText(params.regulatorId),
        }
  const requestParams = Object.fromEntries(Object.entries(normalizedParams).filter(([, value]) => Boolean(value)))

  if (Object.keys(requestParams).length === 0) {
    return undefined
  }

  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikilicenseGo : api.v4,
    url: REGULATOR_CODE_BY_EN_SHORT_NAME_API_PATH,
    method: 'GET' as const,
    params: requestParams,
  }

  const response = import.meta.server
    ? await useServerFetch<RegulatorCodeResponse>(requestConfig)
    : await request<unknown, RegulatorCodeResponse>(requestConfig)

  return response?.data || response
}

/** 根据监管机构 urlSlug、code 或 id 获取监管机构 code。 */
export const getRegulatorCodeByEnShortName = async (params: RegulatorCodeResolveParams | string): Promise<string> => {
  const source = await getRegulatorResolveInfo(params)

  return normalizeText(source?.regulatorCode)
}

/** 根据监管机构 code 解析列表详情路由所需的 urlSlug。 */
export const getRegulatorUrlSlugByCode = async (regulatorCode: string): Promise<string> => {
  const source = await getRegulatorResolveInfo({ regulatorCode })

  return normalizeText(source?.urlSlug)
}
